import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../../components/base/Layout';
import { products } from '../../mocks/products';

export default function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const product = products.find(p => p.id === id);
  const [activeTab, setActiveTab] = useState('overview');
  const [activeImage, setActiveImage] = useState(0);

  // Reset state when product changes
  useEffect(() => {
    setActiveTab('overview');
    setActiveImage(0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!product) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h1>
            <Link to="/products" className="text-emerald-600 hover:underline">
              Back to Products
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const allImages = [product.image, ...product.gallery];
  const relatedProducts = products
    .filter(p => p.category === product.category && p.id !== product.id)
    .slice(0, 4);

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'nutrition', label: 'Nutritional Info' },
    { id: 'packaging', label: 'Packaging' },
  ];

  return (
    <Layout>
      {/* Breadcrumb */}
      <div className="bg-emerald-50 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-gray-500 hover:text-emerald-600 transition-colors">
              Home
            </Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <Link to="/products" className="text-gray-500 hover:text-emerald-600 transition-colors">
              Products
            </Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-emerald-600 font-medium">{product.name}</span>
          </nav>
        </div>
      </div>

      {/* Product Hero */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Image Gallery */}
            <div className="space-y-4">
              <div className="relative h-[500px] w-full rounded-2xl overflow-hidden bg-gray-100">
                <img
                  src={allImages[activeImage]}
                  alt={product.name}
                  className="w-full h-full object-cover object-center"
                />
                <span className="absolute top-4 left-4 px-4 py-1.5 bg-emerald-500 text-white text-sm font-semibold rounded-full">
                  {product.category}
                </span>
              </div>
              <div className="grid grid-cols-4 gap-3">
                {allImages.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveImage(index)}
                    className={`relative h-24 w-full rounded-lg overflow-hidden cursor-pointer transition-all duration-300 ${
                      activeImage === index
                        ? 'ring-2 ring-emerald-500 ring-offset-2'
                        : 'opacity-70 hover:opacity-100'
                    }`}
                  >
                    <img
                      src={img}
                      alt={`${product.name} ${index + 1}`}
                      className="w-full h-full object-cover object-center"
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Product Info */}
            <div className="space-y-6">
              <div>
                <span className="text-emerald-600 text-sm font-semibold uppercase tracking-wider">
                  {product.category}
                </span>
                <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-2">{product.name}</h1>
              </div>

              <p className="text-gray-600 leading-relaxed">{product.description}</p>

              {/* Specifications Quick View */}
              <div className="bg-gray-50 rounded-2xl p-6 space-y-4">
                <h3 className="font-semibold text-gray-900">Quick Specifications</h3>
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(product.specifications)
                    .slice(0, 4)
                    .map(([key, value]) => (
                      <div key={key}>
                        <span className="text-xs text-gray-500 uppercase tracking-wider">{key}</span>
                        <p className="text-sm font-medium text-gray-900 mt-1">{value}</p>
                      </div>
                    ))}
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="w-full px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-base font-semibold rounded-full text-center hover:shadow-lg hover:shadow-emerald-500/30 transition-all duration-300 whitespace-nowrap"
                >
                  Request Quote
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Tabs */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Tab Navigation */}
          <div className="flex flex-wrap gap-2 mb-8 border-b border-gray-200 pb-4">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-emerald-500 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="bg-white rounded-2xl p-8 shadow-sm">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-gray-900">Product Overview</h3>
                <p className="text-gray-600 leading-relaxed">{product.description}</p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-4">Full Specifications</h4>
                    <div className="space-y-3">
                      {Object.entries(product.specifications).map(([key, value]) => (
                        <div
                          key={key}
                          className="flex justify-between py-2 border-b border-gray-100"
                        >
                          <span className="text-gray-500 capitalize">
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                          </span>
                          <span className="font-medium text-gray-900">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-4">Key Features</h4>
                    <ul className="space-y-3">
                      <li className="flex items-start gap-3">
                        <i className="ri-check-line text-emerald-500 mt-0.5"></i>
                        <span className="text-gray-600">Premium export quality</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-check-line text-emerald-500 mt-0.5"></i>
                        <span className="text-gray-600">Carefully selected and graded</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-check-line text-emerald-500 mt-0.5"></i>
                        <span className="text-gray-600">Cold chain maintained throughout</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-check-line text-emerald-500 mt-0.5"></i>
                        <span className="text-gray-600">Phytosanitary certified</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-check-line text-emerald-500 mt-0.5"></i>
                        <span className="text-gray-600">Traceable from farm to destination</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'nutrition' && (
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-gray-900">Nutritional Information</h3>
                <p className="text-gray-600">Per 100g serving</p>
                {product.nutritionalInfo ? (
                  <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {Object.entries(product.nutritionalInfo).map(([key, value]) => (
                      <div key={key} className="bg-emerald-50 rounded-xl p-4 text-center">
                        <span className="text-2xl font-bold text-emerald-600">{value}</span>
                        <p className="text-sm text-gray-600 mt-1 capitalize">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500">Nutritional information coming soon.</p>
                )}
              </div>
            )}

            {activeTab === 'packaging' && (
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-gray-900">Packaging Options</h3>
                <p className="text-gray-600">{product.specifications.packaging}</p>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-gray-50 rounded-xl p-6 text-center">
                    <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-4">
                      <i className="ri-box-3-line text-2xl text-emerald-600"></i>
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">Carton Boxes</h4>
                    <p className="text-sm text-gray-600">
                      Standard export cartons with proper ventilation
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-6 text-center">
                    <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-4">
                      <i className="ri-shopping-bag-line text-2xl text-emerald-600"></i>
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">Mesh Bags</h4>
                    <p className="text-sm text-gray-600">
                      Breathable mesh packaging for optimal freshness
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-6 text-center">
                    <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-4">
                      <i className="ri-stack-line text-2xl text-emerald-600"></i>
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">Palletized</h4>
                    <p className="text-sm text-gray-600">
                      Ready for container loading and shipping
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Related <span className="text-emerald-500">Products</span>
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map(relatedProduct => (
                <Link
                  key={relatedProduct.id}
                  to={`/product/${relatedProduct.id}`}
                  className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
                >
                  <div className="relative h-48 w-full overflow-hidden">
                    <img
                      src={relatedProduct.image}
                      alt={relatedProduct.name}
                      className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 group-hover:text-emerald-600 transition-colors">
                      {relatedProduct.name}
                    </h3>
                    <span className="text-sm text-gray-500">{relatedProduct.category}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
}
